import moment from "moment";
import axios from "axios";

const CASE_STATUSES: {
    [key: number]: { name: string, className: string }
} = {
    0: {
        name: "pending",
        className: 'pending'
    },
    1: {
        name: "in process",
        className: 'inProcess'
    },
    2: {
        name: "matched",
        className: 'matched'
    },
    3: {
        name: "waiting approval",
        className: 'waitingApproval'
    },
    4: {
        name: "closed",
        className: 'closed'
    },
    5: {
        name: "deleted",
        className: 'deleted'
    },
    6: {
        name: "canceled",
        className: 'canceled'
    },
    7: {
        name: "rejected",
        className: 'rejected'
    },
    8: {
        name: "admin approved",
        className: 'adminApproved'
    },
    9: {
        name: "admin rejected",
        className: 'adminRejected'
    },
    // The unmatch (reject-matched-attorney) states. Appended, deliberately leaving every
    // existing entry alone - 5 and 6 read the opposite way round to the server's
    // CASE_STATUSES and the user-facing copy follows THIS map, so correcting them here
    // would silently move the copy too. Separate concern, separate change.
    //
    // No new colours: 20 borrows the waitingApproval swatch (it is also "someone else
    // has to answer") and 21 the rejected one, both already in _base.scss.
    20: {
        name: "unmatch pending",
        className: 'waitingApproval'
    },
    21: {
        name: "unmatch disputed",
        className: 'rejected'
    },
};

/**
 * The numeric case statuses as the SERVER defines them
 * (server/src/v1/modules/case/config.json -> CASE_STATUSES).
 *
 * Use these for LOGIC. The CASE_STATUSES map above is a label/colour lookup only, and its
 * 5 and 6 entries are named the other way round from the server's (it calls 5 "deleted"
 * and 6 "canceled"); the notificationBox copy follows those names, so the display side is
 * left exactly as it was. Nothing here changes what is rendered.
 */
export const CASE_STATUS = {
    pending: 0,
    inProcess: 1,
    matched: 2,
    inRevision: 3,
    closed: 4,
    canceled: 5,
    deleted: 6,
    rejected: 7,
    closedByAdmin: 8,
    rejectedByAdmin: 9,
    unmatchPending: 20,
    unmatchDisputed: 21
};

/** Mon-Fri 09:00-17:00 only - see UNMATCH_WINDOW_HOURS on the server. */
export const UNMATCH_WINDOW_HOURS = 6;

/** The creation minimum the resubmit endpoint re-applies (config.json -> MIN_PRICE). */
export const MIN_CASE_PRICE = 270;

/** The description length the resubmit endpoint re-applies (config.json -> MIN_COMMENT_LENGTH). */
export const MIN_COMMENT_LENGTH = 40;

export const PAYMENT_TYPES: { [key: string]: string } = {
    'single_appearance': 'Single Appearance, Flat Fee',
    'contingency': 'Contingency Basis',
    'pro_bono': 'Pro Bono Basis',
    'full_case_hourly': 'Full Case, Hourly',
    'full_case_flat': 'Full Case, Flat Fee',
};

interface Default {
    id: number
    name: string,
}

export const LANGUAGES:
    Array<Default>
    = [
    {
        id: 1,
        name: "English"
    },
    {
        id: 2,
        name: "Spanish"
    }
];


export const Initials = (name: string) => {
    if (!name) return '';
    const initials = name.match(/\b\w/g) || [];
    return ((initials.shift() || '') + (initials.pop() || '')).toUpperCase();
};

export const s3ImageUrlMutate = (url: string, preset = '200x200') => {
    if (!url) return '';
    const split = url.split('/');
    split.splice(0, 2);
    split.splice(split.length - 1, 0, preset);
    return `https://${split.join('/')}`;
};

export const caseCardClass = (status: number) => {
    return CASE_STATUSES[status];
};

export const yearsOfExpierence = (date: any) => {
    return date ? moment().diff(date, 'years', false) : 'N/A';
};

export const formatDate = (date: any, format: string) => {
    return date ? moment(date).format(format) : 'N/A'
};

export const downloadFile = async (e: any, imageUrl: string, name: string) => {
    e.preventDefault();
    e.stopPropagation();
    const response = await axios.get(imageUrl, {
        headers: {
            "Content-Type": "application/octet-stream",
            "Access-Control-Allow-Origin": "*"
        },
        responseType: "blob"
    });
    const a = document.createElement("a");
    const url = window.URL.createObjectURL(response.data);
    a.href = url;
    a.download = name;
    return a.click();
};

export const isMobileBrowser = (): boolean => window.innerWidth <= 595;

export const getObjectKeyName = (data: { [key: string]: any }, value: string) => {
    return Object.keys(data).find(key => key === value);
};

interface ScrollTo {
    top?: number,
    left?: number,
    behavior: "smooth" | "auto"
}

export const scrollTo = (data: ScrollTo): void => window.scrollTo(data);


export const roundFloatNumber = (num: number) => (Math.floor(num * 100) / 100);

export const calcStripeFee = (val: number) => {

    const cents = (val * 100);
    const stripeFee = 2.9;
    const stripeCent = 30;

    return roundFloatNumber(((cents * stripeFee / 100) + stripeCent)) / 100;
};

export const formatPhoneNumber = (string: string): string | null => {
    let cleaned: string = ('' + string).replace(/\D/g, '');
    let match: RegExpMatchArray | null = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
        return `(${match[1]}) ${match[2]}-${match[3]}`
    }
    return null
};

export const  scrollToElemError = () => {
    const errorsList = document.getElementsByClassName('Mui-error');
    if (errorsList && errorsList.length) {
        const parent = errorsList[0].parentElement;
        parent && parent.scrollIntoView({behavior: 'smooth', block: 'start'});
    }
    return;
};

// generate unique id for local usage
export const stringGenerator = (length: number): string => {
    let result = '',
        characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789',
        charactersLength = characters.length;
    for (let i = 0; i < length; i++) {
        result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return result;
};

// get object from array by its id
export const getObjectById = (array: Array<any>, value: string | number)  => {
    return array.find(el => el.id === value)
};

export const getCourtDate = (timeZone: string = 'America/Los_Angeles') => {
    return new Date().toLocaleString('en-US', {
        timeZone: timeZone
    });
};
